/**
 * Синтез звуков в браузере (Web Audio): шум или тон с фильтром, огибающей и сдвигом высоты. Свои звуки без чужих
 * записей — без вопросов прав (правило проекта «игра своя, не копия») и без скачивания файлов. Один и тот же рецепт
 * играет и вживую (AudioContext), и в съёмке (OfflineAudioContext) — ролик звучит, как игра.
 */

/** Голос рецепта: шум или тон, когда начать (с), длина (с), громкость; фильтр и высота — от и до за длину. */
export interface Voice {
  readonly kind: "noise" | "tone";
  readonly at?: number;
  readonly duration: number;
  readonly gain: number;
  readonly filter?: {
    readonly type: BiquadFilterType;
    readonly from: number;
    readonly to: number;
    readonly q?: number;
  };
  /** Тон: частота от и до, Гц; форма волны. */
  readonly pitch?: { readonly from: number; readonly to: number };
  readonly wave?: OscillatorType;
  /** Нарастание, с (по умолчанию 3 мс — щелчка нет, удар резкий). */
  readonly attack?: number;
  /** Отзвуки, как в зале: сколько повторов, через сколько секунд, во сколько раз тише каждый. */
  readonly echo?: { readonly count: number; readonly delay: number; readonly decay: number };
}

export type Recipe = readonly Voice[];

const NOISE_SECONDS = 1;

/** Белый шум на секунду — детерминированный (свой ГСЧ): съёмка звучит одинаково при каждом прогоне. */
export function noiseBuffer(context: BaseAudioContext): AudioBuffer {
  const buffer = context.createBuffer(1, Math.round(context.sampleRate * NOISE_SECONDS), context.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 0x2545f491;
  for (let index = 0; index < data.length; index++) {
    seed = (Math.imul(seed, 1103515245) + 12345) >>> 0;
    data[index] = (seed / 0xffffffff) * 2 - 1;
  }
  return buffer;
}

/** Сыграть рецепт в момент when (с, по часам контекста) с громкостью scale в узел destination. */
export function playRecipe(
  context: BaseAudioContext,
  destination: AudioNode,
  recipe: Recipe,
  when: number,
  scale: number,
  noise: AudioBuffer,
  stretch = 1,
): void {
  // stretch > 1 — замедление: звук длиннее и ниже (растяжение, а не только тише)
  for (const voice of expand(recipe)) {
    const start = when + (voice.at ?? 0) * stretch;
    const end = start + voice.duration * stretch;
    const gain = context.createGain();
    const peak = Math.max(0.0001, voice.gain * scale);
    const attack = voice.attack ?? 0.002;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    let source: AudioScheduledSourceNode;
    if (voice.kind === "noise") {
      const player = context.createBufferSource();
      player.buffer = noise;
      player.loop = true;
      source = player;
    } else {
      const oscillator = context.createOscillator();
      oscillator.type = voice.wave ?? "sine";
      const pitch = voice.pitch ?? { from: 220, to: 220 };
      oscillator.frequency.setValueAtTime(pitch.from / stretch, start);
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, pitch.to / stretch), end);
      source = oscillator;
    }
    let tail: AudioNode = source;
    if (voice.filter) {
      const filter = context.createBiquadFilter();
      filter.type = voice.filter.type;
      filter.Q.value = voice.filter.q ?? 0.8;
      filter.frequency.setValueAtTime(voice.filter.from, start);
      filter.frequency.exponentialRampToValueAtTime(Math.max(1, voice.filter.to), end);
      source.connect(filter);
      tail = filter;
    }
    tail.connect(gain);
    gain.connect(destination);
    source.start(start);
    source.stop(end + 0.02);
  }
}

/** Отзвуки голоса — его же копии позже и тише. */
function expand(recipe: Recipe): Voice[] {
  return recipe.flatMap((voice) => {
    const echo = voice.echo;
    if (!echo) return [voice];
    return Array.from({ length: echo.count + 1 }, (_, index) => ({
      ...voice,
      at: (voice.at ?? 0) + echo.delay * index,
      gain: voice.gain * echo.decay ** index,
    }));
  });
}
